import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import InstallComponentDialog from './InstallComponentDialog';
import RemoveComponentDialog from './RemoveComponentDialog';

/**
 * Draggable, editable installation-point row.
 *
 * Renders as a read-only label until the user clicks the pencil, at which
 * point the label swaps to an input. Drag handle on the left, delete on the
 * right. Only visible to admins/techs.
 */
const SortableRow = ({ point, canEdit, onRename, onDelete, onInstall, onRemove, onOpenComponent }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: point.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(point.label);

  useEffect(() => {
    setDraft(point.label);
  }, [point.label]);

  const commit = async () => {
    const trimmed = (draft || '').trim();
    if (!trimmed) {
      toast.error('Label cannot be empty');
      setDraft(point.label);
      setEditing(false);
      return;
    }
    if (trimmed === point.label) {
      setEditing(false);
      return;
    }
    const ok = await onRename(point.id, trimmed);
    if (ok) setEditing(false);
  };

  const occupant = point.current_component;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded-md p-2 bg-white space-y-1.5"
      data-testid={`installation-point-row-${point.id}`}
    >
      {/* Top row: drag handle + label + rename/delete controls */}
      <div className="flex items-center gap-2">
        {canEdit && (
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing px-1"
            title="Drag to reorder"
            data-testid={`installation-point-drag-${point.id}`}
            {...attributes}
            {...listeners}
          >
            ⋮⋮
          </button>
        )}

        {editing ? (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setDraft(point.label); setEditing(false); }
            }}
            className="h-8 text-sm flex-1"
            data-testid={`installation-point-input-${point.id}`}
          />
        ) : (
          <button
            type="button"
            className="flex-1 text-left text-sm font-medium text-slate-800 truncate hover:text-slate-900"
            onClick={() => canEdit && setEditing(true)}
            disabled={!canEdit}
            data-testid={`installation-point-label-${point.id}`}
          >
            {point.label}
          </button>
        )}

        {canEdit && !editing && (
          <>
            <button
              type="button"
              className="text-xs text-slate-500 hover:text-slate-700 px-1"
              onClick={() => setEditing(true)}
              data-testid={`installation-point-edit-${point.id}`}
              title="Rename"
            >
              Rename
            </button>
            <button
              type="button"
              className="text-xs text-red-600 hover:text-red-800 px-1 disabled:text-slate-300 disabled:hover:text-slate-300 disabled:cursor-not-allowed"
              onClick={() => onDelete(point.id, point.label)}
              disabled={!!occupant}
              title={occupant ? 'Remove the installed part before deleting this point' : 'Delete'}
              data-testid={`installation-point-delete-${point.id}`}
            >
              Delete
            </button>
          </>
        )}
      </div>

      {/* Bottom row: occupant (component) + install/swap/remove buttons */}
      <div className="flex items-center gap-2 flex-wrap pl-6 text-sm">
        {occupant ? (
          <>
            <button
              type="button"
              onClick={() => onOpenComponent(occupant.id)}
              className="flex-1 min-w-0 text-left hover:bg-slate-50 rounded px-1.5 py-0.5 transition"
              data-testid={`installation-point-occupant-${point.id}`}
              title="Open serialized component detail"
            >
              <span className="font-mono text-xs bg-blue-50 text-blue-800 rounded px-1.5 py-0.5 mr-1.5">
                SN: {occupant.serial_number}
              </span>
              <span className="text-slate-700">{occupant.name}</span>
              {occupant.mfr_part_number && (
                <span className="ml-1.5 text-xs text-slate-500 font-mono">
                  ({occupant.mfr_part_number})
                </span>
              )}
              {occupant.installed_at && (
                <span className="ml-2 text-xs text-slate-500">
                  since {new Date(occupant.installed_at).toLocaleDateString()}
                </span>
              )}
            </button>
            {canEdit && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onRemove(point, occupant)}
                data-testid={`installation-point-remove-btn-${point.id}`}
              >
                Swap / Remove
              </Button>
            )}
          </>
        ) : (
          <>
            <span className="text-xs text-slate-400 flex-1">
              No serialized part installed
            </span>
            {canEdit && (
              <Button
                type="button"
                size="sm"
                onClick={() => onInstall(point)}
                data-testid={`installation-point-install-btn-${point.id}`}
              >
                Install Part
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Collapsible list of named installation points for a piece of equipment.
 * Admins/techs can add, rename, delete, and drag-reorder. Viewers see the
 * list read-only.
 *
 * Part 2 (serialized components) will layer install/swap events on top of
 * these points.
 */
const InstallationPointsPanel = ({ machineryId, equipmentName, canEdit, refreshKey }) => {
  const navigate = useNavigate();
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(`ipPanelCollapsed:${machineryId}`) === '1';
    } catch {
      return false;
    }
  });
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [installTarget, setInstallTarget] = useState(null); // point being installed to
  const [removeTarget, setRemoveTarget] = useState(null);   // {point, component}

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchPoints = async () => {
    try {
      const res = await axios.get(`${API}/machinery/${machineryId}/installation-points`);
      setPoints(res.data || []);
    } catch (e) {
      toast.error('Failed to load installation points');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoints();
  }, [machineryId, refreshKey]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(`ipPanelCollapsed:${machineryId}`, next ? '1' : '0');
      } catch { /* noop */ }
      return next;
    });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/machinery/${machineryId}/installation-points`,
        { label }
      );
      setPoints((prev) => [...prev, res.data]);
      setNewLabel('');
      toast.success('Installation point added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add installation point');
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (id, label) => {
    try {
      const res = await axios.put(`${API}/installation-points/${id}`, { label });
      setPoints((prev) => prev.map((p) => (p.id === id ? res.data : p)));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename installation point');
      return false;
    }
  };

  const handleDelete = async (id, label) => {
    const ok = window.confirm(
      `Delete installation point "${label}"?\n\nHistorical logs that referenced this point will keep their reference for the audit trail but the point itself will be gone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/installation-points/${id}`);
      setPoints((prev) => prev.filter((p) => p.id !== id));
      toast.success('Installation point deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete installation point');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = points.findIndex((p) => p.id === active.id);
    const newIndex = points.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(points, oldIndex, newIndex).map((p, idx) => ({
      ...p,
      sort_order: idx,
    }));
    setPoints(reordered); // optimistic

    try {
      await axios.post(
        `${API}/machinery/${machineryId}/installation-points/reorder`,
        { items: reordered.map((p) => ({ id: p.id, sort_order: p.sort_order })) }
      );
    } catch (err) {
      toast.error('Failed to save order — reloading');
      fetchPoints();
    }
  };

  return (
    <Card data-testid="installation-points-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="flex items-center gap-2 text-left"
            data-testid="installation-points-toggle"
            aria-expanded={!collapsed}
          >
            <span className="text-slate-400 text-sm w-4">
              {collapsed ? '▸' : '▾'}
            </span>
            <CardTitle className="text-base">
              Installation Points
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({points.length})
              </span>
            </CardTitle>
          </button>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent data-testid="installation-points-body" className="space-y-3">
          {loading ? (
            <div className="text-sm text-slate-500 italic">Loading…</div>
          ) : points.length === 0 ? (
            <div className="text-sm text-slate-500 italic">
              No installation points yet. Add one below to label where serialized parts
              (valves, head covers, etc.) will be installed on this equipment.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={points.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1.5" data-testid="installation-points-list">
                  {points.map((point) => (
                    <SortableRow
                      key={point.id}
                      point={point}
                      canEdit={canEdit}
                      onRename={handleRename}
                      onDelete={handleDelete}
                      onInstall={(p) => setInstallTarget(p)}
                      onRemove={(p, occ) => setRemoveTarget({ point: p, component: occ })}
                      onOpenComponent={(id) => navigate(`/inventory/serialized/${id}`)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          {canEdit && (
            <form onSubmit={handleAdd} className="flex gap-2 pt-2 border-t">
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder='e.g. "Stage 2 Suction Port", "Rack 3 Slot B"'
                className="h-9 text-sm"
                data-testid="installation-point-new-input"
                disabled={adding}
              />
              <Button
                type="submit"
                size="sm"
                disabled={adding || !newLabel.trim()}
                data-testid="installation-point-add-btn"
              >
                {adding ? 'Adding…' : 'Add'}
              </Button>
            </form>
          )}
        </CardContent>
      )}

      <InstallComponentDialog
        open={!!installTarget}
        installationPoint={installTarget}
        equipmentName={equipmentName}
        onClose={() => setInstallTarget(null)}
        onSuccess={() => fetchPoints()}
      />
      <RemoveComponentDialog
        open={!!removeTarget}
        component={removeTarget?.component}
        installationPoint={removeTarget?.point}
        equipmentName={equipmentName}
        onClose={() => setRemoveTarget(null)}
        onSuccess={() => fetchPoints()}
      />
    </Card>
  );
};

export default InstallationPointsPanel;
